import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { EMPTY, Subscription, timer } from 'rxjs';
import { catchError, switchMap, takeWhile } from 'rxjs/operators';

import { AuthService } from '../../shared/auth.service';
import { ClientCvTemplateService } from '../templates/client-cv-template.service';
import { ClientCvTemplate } from '../../interfaces/client-cv-template.interface';
import { BaseLayout } from '../../interfaces/base-layout.interface';

export type StudioSelection =
  | { type: 'builtin'; layout: BaseLayout }
  | { type: 'custom'; template: ClientCvTemplate }
  | { type: 'theme' }
  | { type: 'house' }
  | { type: 'upload'; prefilledLayoutId?: string; replaceTemplateId?: number; replaceTemplateName?: string };

@Component({
  selector: 'bst-cv-studio',
  templateUrl: './studio.component.html',
  styleUrls: ['./studio.component.scss'],
  standalone: false,
})
export class StudioComponent implements OnInit, OnDestroy {
  public clientId!: number;
  public baseLayouts: BaseLayout[] = [];
  public templates: ClientCvTemplate[] = [];
  public selection: StudioSelection | null = null;
  public loading = true;
  public error: string | null = null;
  private subs: Subscription[] = [];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly auth: AuthService,
    private readonly service: ClientCvTemplateService,
  ) {}

  public get parentCvsLink(): unknown[] {
    const url = this.router.url;
    if (url === '/cv-generator' || url.startsWith('/cv-generator/')) return ['/cv-generator'];
    return ['/clients', this.route.parent?.snapshot.paramMap.get('id') ?? '', 'cvs'];
  }

  ngOnInit(): void {
    this.clientId = this.resolveClientId();
    this.fetch();
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  public select(s: StudioSelection): void {
    this.selection = s;
  }

  public onUseAsBase(layoutId: string): void {
    this.selection = { type: 'upload', prefilledLayoutId: layoutId };
  }

  public onReplaceFiles(template: ClientCvTemplate): void {
    this.selection = {
      type: 'upload',
      prefilledLayoutId: template.base_layout_id,
      replaceTemplateId: template.id,
      replaceTemplateName: template.name,
    };
  }

  public onTemplateCreated(t: ClientCvTemplate): void {
    this.fetchTemplates(t.id);
  }

  public onUploadCancelled(): void {
    this.selection = null;
  }

  public onTemplateUpdated(updated: ClientCvTemplate): void {
    this.templates = this.templates.map(t => t.id === updated.id ? updated : t);
    if (this.selection?.type === 'custom' && this.selection.template.id === updated.id) {
      this.selection = { type: 'custom', template: updated };
    }
  }

  public onTemplateDeleted(id: number): void {
    this.templates = this.templates.filter(t => t.id !== id);
    if (this.selection?.type === 'custom' && this.selection.template.id === id) {
      this.selection = null;
    }
  }

  private fetch(): void {
    this.loading = true;
    this.error = null;
    const sub = this.service.listBaseLayouts().subscribe({
      next: layouts => {
        this.baseLayouts = layouts;
        this.fetchTemplates();
      },
      error: err => {
        console.error('studio: load layouts failed', err);
        this.error = 'Failed to load layouts';
        this.loading = false;
      },
    });
    this.subs.push(sub);
  }

  private fetchTemplates(selectId?: number): void {
    const sub = this.service.list(this.clientId).subscribe({
      next: page => {
        this.templates = page.data;
        this.loading = false;
        if (selectId) {
          const fresh = page.data.find(t => t.id === selectId);
          if (fresh) this.selection = { type: 'custom', template: fresh };
        }
        if (page.data.some(t => t.status === 'processing')) this.startPolling();
      },
      error: err => {
        console.error('studio: load templates failed', err);
        this.error = 'Failed to load templates';
        this.loading = false;
      },
    });
    this.subs.push(sub);
  }

  private startPolling(): void {
    // Swallow transient errors with EMPTY (not an empty page) so takeWhile
    // never sees a `data: []` and stops the timer prematurely. A sustained
    // outage will manifest as templates staying in `processing` — operator
    // can refresh manually. Logging only, no toast: this is a background
    // refresh.
    const sub = timer(5000, 5000).pipe(
      switchMap(() => this.service.list(this.clientId).pipe(
        catchError(err => {
          console.error('studio: polling refresh failed', err);
          return EMPTY;
        }),
      )),
      takeWhile(page => page.data.some(t => t.status === 'processing'), true),
    ).subscribe(page => {
      this.templates = page.data;
      const cur = this.selection;
      if (cur?.type === 'custom') {
        const fresh = page.data.find(t => t.id === cur.template.id);
        if (fresh) this.selection = { type: 'custom', template: fresh };
      }
    });
    this.subs.push(sub);
  }

  private resolveClientId(): number {
    const id = this.route.snapshot.parent?.paramMap.get('id');
    if (id) return parseInt(id, 10);
    const u = this.auth.user.getValue();
    return u?.client?.id ?? 0;
  }
}
